import { ChevronDown20Regular, ChevronRight20Regular } from "@fluentui/react-icons";
import type { AnalysisResultData } from "../../../../services/AnalysisResultService";
import { normalizeSamples } from "../utils";
import { GRADIENT_UNIT } from "../resultOrderSelection";
import { HorizontalScrollArea } from "./HorizontalScrollArea";
import {
  containerClassName,
  displayResultValue,
  formatChemistryRowLabel,
} from "./shared";

type NormalizedSample = ReturnType<typeof normalizeSamples>[number];

interface ChemistrySummaryRow {
  rowLabel: string;
  sampleValues: Record<string, string>;
}

interface ResultsSummarySectionProps {
  summaryExpanded: boolean;
  onToggle: () => void;
  resultData: AnalysisResultData;
  chemistrySummaryRows: ChemistrySummaryRow[];
  normalizedSamples: NormalizedSample[];
}

export function ResultsSummarySection({
  summaryExpanded,
  onToggle,
  resultData,
  chemistrySummaryRows,
  normalizedSamples,
}: ResultsSummarySectionProps) {
  const emptyColumns = Array.from({ length: Math.max(0, 6 - normalizedSamples.length) }, (_, index) => index);

  return (
    <section className={containerClassName}>
      <button
        type="button"
        className="flex h-14 w-full items-center justify-between px-5 text-left"
        aria-expanded={summaryExpanded}
        onClick={onToggle}
      >
        <div>
          <div className="text-[18px] font-bold text-primary-dark dark:text-primary-light">Results summary</div>
        </div>
        {summaryExpanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
      </button>
      {summaryExpanded && (
        <>
          <div className="px-2.5">
            <HorizontalScrollArea scrollAreaId="analysis-result-summary-grid">
              <table className="w-full min-w-[700px] table-fixed border-collapse bg-primary-dark/5 dark:bg-black/20 text-center text-[14px] leading-[18px] [&_th]:border-2 [&_th]:border-primary-dark/10 dark:[&_th]:border-oi-dark-background [&_th]:font-normal [&_td]:border-2 [&_td]:border-primary-dark/10 dark:[&_td]:border-oi-dark-background">
                <colgroup>
                  <col className="w-[14.2%]" />
                  {(["density", "hc", "co", "o", "coM", "mm", "mi", "ma", "gradient"] as const).map(field => <col key={`summary-col-${field}`} className="w-[9.53%]" />)}
                </colgroup>
                <thead className="h-[50px] bg-primary-dark/5 dark:bg-black/20 text-primary-dark dark:text-primary-light">
                  <tr>
                    <th className="px-2 py-1">Sample</th>
                    <th className="px-2 py-1">Density<div className="text-[14px] text-primary-dark dark:text-primary-light">(g/cm³)</div></th>
                    <th className="px-2 py-1">Hc<div className="text-[14px] text-primary-dark dark:text-primary-light">(kA/m)</div></th>
                    <th className="px-2 py-1">Co %<div className="text-[14px] text-primary-dark dark:text-primary-light">(wt%)</div></th>
                    <th className="px-2 py-1">σ<sub>s</sub><div className="text-[14px] text-primary-dark dark:text-primary-light">(µTm³/kg)</div></th>
                    <th className="px-2 py-1">CoM<div className="text-[14px] text-primary-dark dark:text-primary-light">(Auto)</div></th>
                    <th className="px-2 py-1">MM<div className="text-[14px] text-primary-dark dark:text-primary-light">(Auto)</div></th>
                    <th className="px-2 py-1">Mi<div className="text-[14px] text-primary-dark dark:text-primary-light">(A-B-C)</div></th>
                    <th className="px-2 py-1">Ma</th>
                    <th className="px-2 py-1">Gradient ({GRADIENT_UNIT})</th>
                  </tr>
                </thead>
                <tbody>
                  {resultData.summaryRows.map(row => (
                    <tr key={row.sampleId} className="h-[50px]">
                      <td className="whitespace-nowrap px-2.5 text-left text-primary-dark dark:text-primary-light">{row.sampleId}</td>
                      {(["density", "hc", "co", "o", "coM", "mm", "mi", "ma", "gradient"] as const).map(field => (
                        <td key={field} className="px-2 font-bold text-oi-button-blue dark:text-oi-powder-form-yellow">
                          <div className="text-center">{displayResultValue(row[field])}</div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </HorizontalScrollArea>

            <div className="mt-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2 text-[12px] text-primary-dark dark:text-primary-light">
              <div className="flex min-h-8 items-center gap-2 rounded border border-oi-button-blue bg-oi-button-blue/10 px-1.5 py-1">
                <span aria-hidden="true" className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-oi-button-blue text-[14px] text-oi-button-blue">i</span>
                <span>{resultData.summaryNote}</span>
              </div>
              <div className="grid gap-2 pr-12 pt-1 text-right">
                <div className="flex items-center justify-end gap-4">
                  <span>Temperature of water for density measurement (°C)</span>
                  <span className="w-7 text-[14px] font-bold text-oi-button-blue dark:text-oi-powder-form-yellow">{displayResultValue(resultData.densityMeasurementTemperature)}</span>
                </div>
                <div className="flex items-center justify-end gap-4">
                  <span>Correction factor for density measurement</span>
                  <span className="w-7 text-[14px] font-bold text-oi-button-blue dark:text-oi-powder-form-yellow">{displayResultValue(resultData.densityCorrectionFactor)}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="pl-2.5 pr-6 pb-6">
            <div className="mb-4 px-2 text-[18px] leading-[22px] font-bold text-primary-dark dark:text-primary-light">Summary chemical analysis</div>
            <HorizontalScrollArea scrollAreaId="analysis-result-chemistry-grid">
              <table style={{ minWidth: Math.max(700, 128 + normalizedSamples.length * 160) }} className="w-full min-w-[700px] table-fixed border-collapse bg-primary-dark/5 dark:bg-black/20 text-center text-[14px] leading-[18px] [&_th]:border-2 [&_th]:border-primary-dark/10 dark:[&_th]:border-oi-dark-background [&_th]:font-normal [&_td]:border-2 [&_td]:border-primary-dark/10 dark:[&_td]:border-oi-dark-background">
                <colgroup>
                  <col className="w-[11.4%]" />
                  {normalizedSamples.map(sample => <col key={`chemistry-col-${sample.sampleId}`} className="w-[15.8%]" />)}
                  {emptyColumns.map(index => <col key={`empty-${index}`} className={index === emptyColumns.length - 1 ? "w-[9.6%]" : "w-[15.8%]"} />)}
                </colgroup>
                <thead className="h-[50px] bg-primary-dark/5 dark:bg-black/20 text-primary-dark dark:text-primary-light">
                  <tr>
                    <th className="px-2 py-1">Element</th>
                    {normalizedSamples.map(sample => <th key={sample.sampleId} className="px-2 py-1">{sample.sampleId}</th>)}
                    {emptyColumns.map(index => <th key={`empty-${index}`} aria-hidden="true" />)}
                  </tr>
                </thead>
                <tbody>
                  {chemistrySummaryRows.map(row => (
                    <tr key={row.rowLabel} className="h-[37px]">
                      <td className="px-2 text-center text-primary-dark dark:text-primary-light">{formatChemistryRowLabel(row.rowLabel)}</td>
                      {normalizedSamples.map(sample => (
                        <td key={`${row.rowLabel}-${sample.sampleId}`} className="px-2 font-bold text-oi-button-blue dark:text-oi-powder-form-yellow">
                          <div className="text-center">{displayResultValue(row.sampleValues[sample.sampleId])}</div>
                        </td>
                      ))}
                      {emptyColumns.map(index => <td key={`empty-${index}`} aria-hidden="true" />)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </HorizontalScrollArea>
          </div>
        </>
      )}
    </section>
  );
}
